'use client'

import { motion, MotionConfig } from 'motion/react'
import type { ReactNode } from 'react'
import { fadeInContainer, fadeInItem, VIEWPORT } from './animation-tokens'

interface FadeInProps {
  children: ReactNode
  className?: string
  /** Delay before the fade begins, in seconds. */
  delay?: number
}

/**
 * Single-element viewport-triggered fade-in.
 * Use for any standalone block that should fade up when scrolled into view.
 * Under reduced motion the upward travel is dropped and only the fade remains.
 */
export function FadeIn({ children, className, delay }: FadeInProps) {
  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={VIEWPORT}
        variants={fadeInItem}
        transition={delay ? { delay } : undefined}
        className={className}
      >
        {children}
      </motion.div>
    </MotionConfig>
  )
}

/**
 * Stagger-triggered fade-in for a group of children.
 * Each direct child fades in sequentially with the shared stagger timing.
 */
export function FadeInStagger({ children, className }: FadeInProps) {
  return (
    <MotionConfig reducedMotion="user">
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={VIEWPORT}
        variants={fadeInContainer}
        className={className}
      >
        {children}
      </motion.div>
    </MotionConfig>
  )
}

/** Item wrapper for use inside a FadeInStagger. */
export function FadeInStaggerItem({ children, className }: FadeInProps) {
  return (
    <motion.div variants={fadeInItem} className={className}>
      {children}
    </motion.div>
  )
}
